import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Theme support for the app.
 *
 * The app ships in dark mode (the historical default). Light mode is opt-in and
 * is persisted per-browser in localStorage. The active theme is applied by
 * setting `data-theme="light"` on the <html> element; all colors are driven by
 * CSS custom properties that flip under `:root[data-theme="light"]`, so toggling
 * is a single attribute change with no per-component wiring.
 */

export type Theme = 'dark' | 'light';

/** localStorage key holding the user's persisted theme preference. */
export const THEME_STORAGE_KEY = 'orchestrate-theme';

interface ThemeContextValue {
  /** The currently active theme. */
  theme: Theme;
  /** True when light mode is on. Convenience for `theme === 'light'`. */
  isLight: boolean;
  /** Flip between dark and light. */
  toggleTheme: () => void;
  /** Set an explicit theme. */
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Reads the persisted theme, defaulting to dark (light mode off by default). */
export function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode, SSR) — fall back to dark.
  }
  return 'dark';
}

/** Reflects the theme onto the document and persists it. Exported for testability. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'light') {
    root.setAttribute('data-theme', 'light');
  } else {
    root.removeAttribute('data-theme');
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore persistence failures — the in-memory theme still applies.
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isLight: theme === 'light', toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
